import type Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { Layer, Line, Rect, Stage, Text, Transformer } from 'react-konva';

import { findFormat } from '../../config/platforms';
import { useAdsGeneratorStore } from '../../model/store';
import { AdElement, TextElement } from '../../model/types';
import { downloadStage, ExportFormat } from '../../utils/export';
import { ImagePickerModal } from '../Editor/ImagePickerModal';
import { BackgroundNode } from './BackgroundNode';
import { ElementContextMenu } from './ElementContextMenu';
import { SceneNode } from './SceneNode';
import { StageViewport, StageWrapper } from './Stage.styled';
import { TextEditorOverlay } from './TextEditorOverlay';

/** Tolerancja przyciągania liczona w pikselach planszy. */
const SNAP_THRESHOLD = 12;
const VIEWPORT_PADDING = 48;

export interface AdStageHandle {
  download: (options: { scale: number; format: ExportFormat; fileName: string }) => Promise<void>;
  /** Miniatura planszy do zapisu w bibliotece; `maxSize` to dłuższy bok w px. */
  capture: (maxSize: number) => Promise<Blob | null>;
}

interface AdStageProps {
  /** Gdy prawda, plansza sama dopasowuje powiększenie do rozmiaru okna. */
  isFitMode: boolean;
}

export const AdStage = forwardRef<AdStageHandle, AdStageProps>(({ isFitMode }, ref) => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const selectedIds = useAdsGeneratorStore((state) => state.selectedIds);
  const zoom = useAdsGeneratorStore((state) => state.zoom);
  const select = useAdsGeneratorStore((state) => state.select);
  const toggleSelect = useAdsGeneratorStore((state) => state.toggleSelect);
  const duplicateElement = useAdsGeneratorStore((state) => state.duplicateElement);
  const setZoom = useAdsGeneratorStore((state) => state.setZoom);
  const pushHistory = useAdsGeneratorStore((state) => state.pushHistory);
  const updateElement = useAdsGeneratorStore((state) => state.updateElement);

  const stageRef = useRef<Konva.Stage>(null);
  const uiLayerRef = useRef<Konva.Layer>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  /**
   * Prowadnice rysujemy WPROST na węzłach Konvy, bez stanu Reacta.
   *
   * Każde przerysowanie w trakcie przeciągania przywracało elementom pozycje
   * z pamięci i psuło wektor przesunięcia grupy. Trzymanie prowadnic poza
   * Reactem usuwa jedyny powód, dla którego plansza przerysowywała się przy
   * każdym ruchu myszy.
   */
  const verticalGuideRef = useRef<Konva.Line>(null);
  const horizontalGuideRef = useRef<Konva.Line>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; elementId: string } | null>(null);
  const [pickingImageFor, setPickingImageFor] = useState<string | null>(null);

  const width = scene?.width ?? 0;
  const height = scene?.height ?? 0;
  const safeArea = scene ? findFormat(scene.platformId, scene.formatId)?.safeArea : undefined;

  // Dopasowanie do okna liczone z faktycznego rozmiaru kontenera, więc działa
  // też po zwinięciu bocznych paneli.
  const fitToViewport = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || !width || !height) return;
    const available = {
      width: viewport.clientWidth - VIEWPORT_PADDING,
      height: viewport.clientHeight - VIEWPORT_PADDING,
    };
    if (available.width <= 0 || available.height <= 0) return;
    setZoom(Math.min(available.width / width, available.height / height, 1));
  }, [height, setZoom, width]);

  useLayoutEffect(() => {
    if (!isFitMode) return undefined;
    fitToViewport();
    const observer = new ResizeObserver(() => fitToViewport());
    if (viewportRef.current) observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, [fitToViewport, isFitMode]);

  useEffect(() => {
    const transformer = transformerRef.current;
    if (!transformer) return;

    const nodes = editingId
      ? []
      : selectedIds
        .map((id) => stageRef.current?.findOne(`#${id}`))
        .filter((node): node is Konva.Node => Boolean(node));

    transformer.nodes(nodes);
    transformer.getLayer()?.batchDraw();
  }, [selectedIds, editingId, scene?.elements]);

  // Kroje z Google Fonts dojeżdżają po pierwszym rysowaniu, a Konva mierzy tekst
  // dopiero przy rysowaniu, więc po ich wczytaniu wymuszamy odświeżenie planszy.
  useEffect(() => {
    let active = true;
    document.fonts?.ready.then(() => {
      if (active) stageRef.current?.batchDraw();
    });
    return () => {
      active = false;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    download: async (options) => {
      const stage = stageRef.current;
      if (!stage) throw new Error('Plansza nie jest gotowa do zapisu');
      uiLayerRef.current?.hide();
      try {
        await downloadStage(stage, { ...options, zoom });
      } finally {
        uiLayerRef.current?.show();
      }
    },

    capture: async (maxSize) => {
      const stage = stageRef.current;
      if (!stage) return null;
      // `pixelRatio` odwraca powiększenie podglądu, więc miniatura wychodzi
      // w tym samym rozmiarze niezależnie od tego, jak zoom stoi na ekranie.
      const longEdge = Math.max(width, height) || 1;
      uiLayerRef.current?.hide();
      try {
        const blob = await stage.toBlob({
          mimeType: 'image/jpeg',
          quality: 0.8,
          pixelRatio: (maxSize / longEdge) / zoom,
        });
        return blob instanceof Blob ? blob : null;
      } finally {
        uiLayerRef.current?.show();
      }
    },
  }), [height, width, zoom]);

  if (!scene) return null;

  const editingElement = scene.elements.find(
    (element): element is TextElement => element.id === editingId && element.type === 'text',
  );

  const handleStagePointerDown = (event: KonvaEventObject<MouseEvent | TouchEvent>): void => {
    setMenu(null);
    if (event.target === event.target.getStage()) select(null);
  };

  const handleSelect = (id: string, additive: boolean): void => {
    if (additive) toggleSelect(id);
    else if (!selectedIds.includes(id)) select(id);
  };

  /**
   * Punkty, do których przyciągamy przeciągany element: krawędzie i środek
   * planszy oraz krawędzie i środki POZOSTAŁYCH elementów. Bez tych drugich
   * układ trzeba było zestawiać na oko.
   */
  const collectSnapTargets = (): { vertical: number[]; horizontal: number[] } => {
    const vertical = [0, width / 2, width];
    const horizontal = [0, height / 2, height];

    scene.elements.forEach((element) => {
      if (element.hidden || selectedIds.includes(element.id)) return;
      vertical.push(element.x, element.x + element.width / 2, element.x + element.width);
      horizontal.push(element.y, element.y + element.height / 2, element.y + element.height);
    });

    return { vertical, horizontal };
  };

  /** Najbliższy punkt przyciągania dla trzech krawędzi elementu. */
  const bestSnap = (edges: number[], targets: number[]): { target: number; delta: number } | null => {
    let best: { target: number; delta: number; distance: number } | null = null;

    edges.forEach((edge) => targets.forEach((target) => {
      const distance = Math.abs(target - edge);
      if (distance < SNAP_THRESHOLD && (!best || distance < best.distance)) {
        best = { target, delta: target - edge, distance };
      }
    }));

    return best;
  };

  /**
   * Prawy przycisk na elemencie. Blokujemy menu przeglądarki, bo na płótnie nie
   * ma czego kopiować w jej rozumieniu, a pozycję bierzemy z Konvy — jest już
   * policzona względem kontenera planszy, więc nadaje się wprost pod warstwę HTML.
   */
  const handleContextMenu = (elementId: string) => (event: KonvaEventObject<PointerEvent>): void => {
    event.evt.preventDefault();
    select(elementId);

    const position = stageRef.current?.getPointerPosition();
    if (position) setMenu({ x: position.x, y: position.y, elementId });
  };

  const menuElement = menu ? scene.elements.find((element) => element.id === menu.elementId) : undefined;

  /**
   * Start przeciągania.
   *
   * Nie liczymy tu przesunięcia grupy: `Transformer` Konvy sam rozsyła je do
   * wszystkich podpiętych węzłów (`_proxyDrag`). Wcześniej robiliśmy to
   * RÓWNIEŻ ręcznie, więc pozostałe elementy dostawały ruch dwa razy i grupa
   * się rozjeżdżała. Alt duplikuje element w miejscu i przeciąga oryginał.
   */
  const handleDragStart = (element: AdElement) => (event: KonvaEventObject<DragEvent>): void => {
    pushHistory();

    if ((event.evt as MouseEvent)?.altKey) {
      duplicateElement(element.id);
      select(element.id);
    }
  };

  /** Ustawia albo chowa prowadnicę bez udziału Reacta. */
  const drawGuide = (guideRef: React.RefObject<Konva.Line | null>, points: number[] | null): void => {
    const line = guideRef.current;
    if (!line) return;
    if (points) line.points(points);
    line.visible(points !== null);
  };

  const hideGuides = (): void => {
    drawGuide(verticalGuideRef, null);
    drawGuide(horizontalGuideRef, null);
    uiLayerRef.current?.batchDraw();
  };

  const handleDragMove = (event: KonvaEventObject<DragEvent>): void => {
    const node = event.target;
    const box = node.getClientRect({ relativeTo: node.getLayer() ?? undefined });
    const targets = collectSnapTargets();

    const snapX = bestSnap([box.x, box.x + box.width / 2, box.x + box.width], targets.vertical);
    const snapY = bestSnap([box.y, box.y + box.height / 2, box.y + box.height], targets.horizontal);

    // Korektę przyciągania nakładamy tylko na przeciągany węzeł. Transformer
    // przelicza przesunięcie pozostałych z jego pozycji bezwzględnej, więc
    // poprawka rozejdzie się na resztę zaznaczenia sama.
    if (snapX) node.x(node.x() + snapX.delta);
    if (snapY) node.y(node.y() + snapY.delta);

    drawGuide(verticalGuideRef, snapX ? [snapX.target, 0, snapX.target, height] : null);
    drawGuide(horizontalGuideRef, snapY ? [0, snapY.target, width, snapY.target] : null);
    uiLayerRef.current?.batchDraw();
  };

  /**
   * Koniec gestu. Czytamy FAKTYCZNE pozycje węzłów ze sceny, zamiast liczyć je
   * z wektora: po drodze mogło zadziałać przyciąganie i proxy Transformera,
   * więc jedynym pewnym źródłem jest to, co narysowała Konva.
   */
  const handleDragEnd = (element: AdElement) => (): void => {
    hideGuides();

    const ids = selectedIds.includes(element.id) ? selectedIds : [element.id];

    ids.forEach((id) => {
      const node = stageRef.current?.findOne(`#${id}`);
      if (node) updateElement(id, { x: Math.round(node.x()), y: Math.round(node.y()) }, true);
    });
  };

  const handleTransformEnd = (element: AdElement) => (event: KonvaEventObject<Event>): void => {
    const node = event.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    updateElement(element.id, {
      x: Math.round(node.x()),
      y: Math.round(node.y()),
      rotation: Math.round(node.rotation() * 10) / 10,
      width: Math.max(8, Math.round(element.width * scaleX)),
      height: Math.max(8, Math.round(element.height * scaleY)),
    }, true);
  };

  /** Tekst rozlewamy na żywo: skala szerokości od razu wraca do 1, a rośnie `width`. */
  const handleTextTransform = (element: TextElement) => (event: KonvaEventObject<Event>): void => {
    const node = event.target;
    const scaleX = node.scaleX();
    if (scaleX === 1) return;
    node.scaleX(1);
    node.scaleY(1);
    updateElement(element.id, { width: Math.max(24, Math.round(element.width * scaleX)) }, true);
  };

  /** Tekst sam ustala swoją wysokość, więc model dostaje ją z powrotem po zmierzeniu. */
  const handleMeasure = (element: AdElement) => (measured: number): void => {
    // Przy dopasowaniu do ramki wysokość jest ustawieniem użytkownika, więc
    // pomiar jej nie nadpisuje — inaczej ramka rosłaby razem z tekstem i nie
    // byłoby do czego dopasowywać.
    if (element.type === 'text' && element.autoFit) return;
    if (Math.abs(element.height - measured) < 1) return;
    updateElement(element.id, { height: measured }, true);
  };

  const commitText = (text: string): void => {
    if (!editingId) return;
    const current = scene.elements.find((element) => element.id === editingId);
    if (current && current.type === 'text' && current.text !== text) {
      pushHistory();
      updateElement(editingId, { text }, true);
    }
    setEditingId(null);
  };

  const guideStroke = Math.max(1, 1 / zoom);

  return (
    <StageViewport ref={viewportRef}>
      <StageWrapper style={{ width: width * zoom, height: height * zoom }}>
        <Stage
          ref={stageRef}
          width={width * zoom}
          height={height * zoom}
          scaleX={zoom}
          scaleY={zoom}
          onMouseDown={handleStagePointerDown}
          onTouchStart={handleStagePointerDown}
          onContextMenu={(event) => {
            event.evt.preventDefault();
            if (event.target === event.target.getStage()) setMenu(null);
          }}
        >
          <Layer>
            <BackgroundNode background={scene.background} width={width} height={height} />
            {scene.elements.map((element) => (
              <SceneNode
                key={element.id}
                element={element}
                isEditing={editingId === element.id}
                onSelect={handleSelect}
                onDragStart={handleDragStart(element)}
                onTransformStart={pushHistory}
                onDragMove={handleDragMove}
                onDragEnd={handleDragEnd(element)}
                onTransform={element.type === 'text' ? handleTextTransform(element) : undefined}
                onTransformEnd={handleTransformEnd(element)}
                onEditText={setEditingId}
                onMeasure={handleMeasure(element)}
                onContextMenu={handleContextMenu(element.id)}
                onPickImage={setPickingImageFor}
              />
            ))}
          </Layer>

          <Layer ref={uiLayerRef}>
            {safeArea && (
              <>
                <Rect
                  x={safeArea.left}
                  y={safeArea.top}
                  width={width - safeArea.left - safeArea.right}
                  height={height - safeArea.top - safeArea.bottom}
                  stroke="#22d3ee"
                  strokeWidth={guideStroke * 2}
                  dash={[12 / zoom, 10 / zoom]}
                  listening={false}
                />
                {/* Podpis, bo sama ramka nie mówi, czym jest. Siedzi w warstwie
                    pomocniczej, więc nie trafia do eksportowanego pliku. */}
                <Text
                  x={safeArea.left}
                  y={safeArea.top - 26 / zoom}
                  text="strefa bezpieczna"
                  fontSize={16 / zoom}
                  fontFamily="Roboto, Arial, sans-serif"
                  fill="#22d3ee"
                  listening={false}
                />
              </>
            )}
            <Line
              ref={verticalGuideRef}
              points={[0, 0, 0, height]}
              stroke="#ec4899"
              strokeWidth={guideStroke}
              visible={false}
              listening={false}
            />
            <Line
              ref={horizontalGuideRef}
              points={[0, 0, width, 0]}
              stroke="#ec4899"
              strokeWidth={guideStroke}
              visible={false}
              listening={false}
            />
            <Transformer
              ref={transformerRef}
              rotateEnabled
              anchorSize={10 / zoom}
              anchorStrokeWidth={1 / zoom}
              borderStrokeWidth={1 / zoom}
              rotateAnchorOffset={26 / zoom}
              anchorStroke="#0b4f9e"
              anchorFill="#ffffff"
              borderStroke="#0b4f9e"
              keepRatio={false}
              enabledAnchors={selectedIds.length === 1
                && scene.elements.find((element) => element.id === selectedIds[0])?.type === 'text'
                ? ['middle-left', 'middle-right']
                : undefined}
              boundBoxFunc={(oldBox, newBox) => (
                newBox.width < 12 || newBox.height < 12 ? oldBox : newBox
              )}
            />
          </Layer>
        </Stage>

        {menu && menuElement && (
          <ElementContextMenu
            element={menuElement}
            x={menu.x}
            y={menu.y}
            onClose={() => setMenu(null)}
            onEditText={setEditingId}
          />
        )}

        {pickingImageFor && (
          <ImagePickerModal
            onClose={() => setPickingImageFor(null)}
            onPick={(url) => {
              pushHistory();
              updateElement(pickingImageFor, { src: url }, true);
              setPickingImageFor(null);
            }}
          />
        )}

        {editingElement && (
          <TextEditorOverlay
            element={editingElement}
            zoom={zoom}
            onCommit={commitText}
            onCancel={() => setEditingId(null)}
          />
        )}
      </StageWrapper>
    </StageViewport>
  );
});

AdStage.displayName = 'AdStage';
